import { useState } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { completedTopUpCount } from '@mypoz/shared';

import {
  AccountDetailsHeader,
  type AccountDetailsTab,
} from '@/features/wallet/components/AccountDetailsHeader.js';

import { TopUpForm } from '@/features/wallet/components/TopUpForm.js';
import { TopUpHistoryCard } from '@/features/wallet/components/TopUpHistoryCard.js';
import { TransactionsCard } from '@/features/wallet/components/TransactionsCard.js';
import { WalletSummaryCard } from '@/features/wallet/components/WalletSummaryCard.js';
import { useWallet } from '@/features/wallet/index.js';
import { useTopUpHistory } from '@/features/wallet/useTopUpHistory.js';
import { useTransactions } from '@/features/wallet/useTransactions.js';

/**
 * The Wallet page (MYP-54) at `/wallet/balance`: the summary card, the Top up card (online banking
 * through Fiuu, MYP-134; e-wallet / DuitNow QR through Revenue Monster, MYP-135 — each behind its
 * own switch in `config.ts`), and the top-up history
 * (MYP-57), which is real since MYP-107 — `get_trade`, loaded when this page opens.
 *
 * MYP-146: the history is the old website's 账户明细 — two tabs, 所有交易 (every movement of money,
 * the default, as there) and 充值记录 (every top-up attempt). Both load when the page opens: the
 * summary's top-up count needs the second whichever tab is showing.
 */
export function Wallet() {
  const { t } = useTranslation();
  const wallet = useWallet();
  const history = useTopUpHistory();
  const transactions = useTransactions();
  const [tab, setTab] = useState<AccountDetailsTab>('transactions');
  const header = <AccountDetailsHeader tab={tab} onTab={setTab} />;

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-ink">{t('wallet.balance.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('wallet.balance.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 gap-5">
        <WalletSummaryCard
          wallet={wallet}
          // Completed top-ups only; a dash until the history has loaded.
          topUpCount={history.status === 'ready' ? completedTopUpCount(history.records) : undefined}
        />
        <TopUpForm />
        {tab === 'transactions' ? (
          <TransactionsCard
            status={transactions.status}
            records={transactions.records}
            onRetry={transactions.retry}
            header={header}
          />
        ) : (
          <TopUpHistoryCard
            status={history.status}
            records={history.records}
            onRetry={history.retry}
            header={header}
          />
        )}
      </div>
    </>
  );
}
